import { useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  ListOrdersQuery,
  OrderListItemResource,
  OrderSortField,
  OrderStatus,
} from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import {
  DataGrid,
  dayBoundary,
  numberFilter,
  useGridState,
  type GridColumn,
  type GridState,
} from '../../components/data-grid/DataGrid';
import { useAuth } from '../../auth/AuthContext';
import { useOrder, useOrderCounts, useOrders, useUpdateOrderStatus } from '../../api/orders';
import { FlowAnswersList } from '../flow-answers/FlowAnswersList';
import { formatMoney } from '../quotations/quotation-format';

const STATUSES: { value: OrderStatus; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

type Tab = 'ALL' | OrderStatus;

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

function statusTone(status: OrderStatus): string | undefined {
  if (status === 'COMPLETED') return 'var(--color-success)';
  if (status === 'CANCELLED') return 'var(--color-danger)';
  return undefined;
}

/** Grid state -> list query (filters are strings in the grid). */
function toListQuery(tab: Tab, g: GridState): ListOrdersQuery {
  const f = g.filters;
  return {
    page: g.page,
    pageSize: g.pageSize,
    sortBy: g.sortBy as OrderSortField,
    sortOrder: g.sortOrder,
    status: tab === 'ALL' ? undefined : tab,
    customer: f.contactName?.trim() || undefined,
    reference: f.reference?.trim() || undefined,
    totalMin: numberFilter(f.totalAmountMin),
    totalMax: numberFilter(f.totalAmountMax),
    createdFrom: dayBoundary(f.createdAtFrom, false),
    createdTo: dayBoundary(f.createdAtTo, true),
  };
}

/** The expanded "Details" sub-grid: every line, the total, notes and what the flow collected. */
function OrderDetails({ orderId }: { orderId: string }) {
  const detail = useOrder(orderId);
  if (detail.isPending) return <p style={{ margin: 0, ...muted }}>Loading details…</p>;
  if (detail.isError) {
    return (
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)' }}>
        {detail.error.message}
      </p>
    );
  }
  const order = detail.data;
  const money = (n: number) => formatMoney(n, order.currency);
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <div style={{ overflowX: 'auto' }}>
        <table className="ne-table" style={{ minWidth: 480 }}>
          <thead>
            <tr>
              <th>Item name</th>
              <th style={{ textAlign: 'right' }}>Price</th>
              <th style={{ textAlign: 'right' }}>Qty</th>
              <th style={{ textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((item) => (
              <tr key={item.id} style={{ cursor: 'default' }}>
                <td>{item.productName}</td>
                <td style={{ textAlign: 'right' }}>{money(item.unitPriceAmount)}</td>
                <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                <td style={{ textAlign: 'right' }}>
                  {money(item.unitPriceAmount * item.quantity)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ justifySelf: 'end' }}>
        <strong>Order total: {money(order.totalAmount)}</strong>
      </div>
      {order.notes ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          <strong>Notes:</strong> {order.notes}
        </p>
      ) : null}
      <FlowAnswersList metadata={order.metadata} />
    </div>
  );
}

function StatusCell({
  order,
  canManage,
  notify,
}: {
  order: OrderListItemResource;
  canManage: boolean;
  notify: boolean;
}) {
  const toast = useToast();
  const update = useUpdateOrderStatus(order.id);
  if (!canManage) {
    return (
      <span className="ne-badge" style={{ color: statusTone(order.status) }}>
        {order.status}
      </span>
    );
  }
  async function onChange(status: OrderStatus) {
    if (status === order.status) return;
    try {
      await update.mutateAsync({ status, notify });
      toast.success(notify ? 'Status updated — customer notified.' : 'Status updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the order status.');
    }
  }
  return (
    <select
      className="ne-input"
      aria-label={`Status of ${order.reference}`}
      value={order.status}
      disabled={update.isPending}
      onChange={(e) => void onChange(e.target.value as OrderStatus)}
      style={{ color: statusTone(order.status), minWidth: 130 }}
    >
      {STATUSES.map((s) => (
        <option key={s.value} value={s.value}>
          {s.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Orders customers placed through the WhatsApp product-ordering flow
 * (docs/API_SPEC.md section 21), on the standard `DataGrid`: status tabs
 * with counts, a filter under each column, sorting, paging, and a
 * Quotations-style details sub-grid. Changing a status sends the customer
 * a WhatsApp message unless "Notify customer" is switched off.
 */
export function OrdersPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('products.manage');
  const [tab, setTab] = useState<Tab>('ALL');
  const [grid, setGrid] = useGridState({ sortBy: 'createdAt', sortOrder: 'desc' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [notify, setNotify] = useState(true);
  const query = toListQuery(tab, grid);
  const orders = useOrders(query);
  const counts = useOrderCounts(query);

  const toggle = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const columns: GridColumn<OrderListItemResource>[] = [
    {
      key: 'contactName',
      header: 'Customer',
      sortable: true,
      filter: { type: 'text', placeholder: 'Name or phone' },
      render: (o) => (
        <>
          <Link to={`/contacts/${o.contactId}`}>{o.contactName ?? 'Unknown contact'}</Link>
          {o.contactPhone ? <div style={muted}>{o.contactPhone}</div> : null}
        </>
      ),
    },
    {
      key: 'reference',
      header: 'Order ref.',
      filter: { type: 'text', placeholder: 'ORD-…' },
      render: (o) => (
        <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{o.reference}</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Placed',
      sortable: true,
      filter: { type: 'dateRange' },
      render: (o) => new Date(o.createdAt).toLocaleString(),
    },
    {
      key: 'itemCount',
      header: 'Items',
      align: 'right',
      render: (o) => o.itemCount,
    },
    {
      key: 'totalAmount',
      header: 'Total',
      sortable: true,
      align: 'right',
      filter: { type: 'range' },
      render: (o) => <strong>{formatMoney(o.totalAmount, o.currency)}</strong>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (o) => <StatusCell order={o} canManage={canManage} notify={notify} />,
    },
    {
      key: 'actions',
      header: '',
      render: (o) => (
        <Button
          variant="secondary"
          aria-expanded={expanded.has(o.id)}
          aria-label={`${expanded.has(o.id) ? 'Hide' : 'Show'} details of ${o.reference}`}
          onClick={() => toggle(o.id)}
        >
          {expanded.has(o.id) ? 'Hide details' : 'Details'}
        </Button>
      ),
    },
  ];

  const tabs: { id: Tab; label: string; count: number | undefined }[] = [
    { id: 'ALL', label: 'All', count: counts.data?.all },
    ...STATUSES.map((s) => ({ id: s.value as Tab, label: s.label, count: counts.data?.[s.value] })),
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1200 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Orders</h1>
        <span style={{ flex: 1 }} />
        {canManage ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', ...muted }}>
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
            Notify customer on WhatsApp when I change a status
          </label>
        ) : null}
      </div>
      <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
        Orders customers placed through the WhatsApp product-ordering flow. Set up orderable
        products on the <Link to="/products-services">Products & Services</Link> page.
      </p>

      <Card title="Orders">
        <div role="tablist" aria-label="Order status" className="ne-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className="ne-tabs__tab"
              onClick={() => {
                setTab(t.id);
                setGrid({ ...grid, page: 1 });
              }}
            >
              {t.label}
              {t.count !== undefined ? <span className="ne-tabs__count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 'var(--space-3)' }}>
          {orders.isError ? (
            <PageState
              kind="error"
              message={orders.error.message}
              onRetry={() => void orders.refetch()}
            />
          ) : (
            <DataGrid
              columns={columns}
              rows={orders.data?.items ?? []}
              rowKey={(o) => o.id}
              rowLabel={(o) => o.reference}
              total={orders.data?.meta.total ?? 0}
              state={grid}
              onStateChange={setGrid}
              loading={orders.isFetching}
              emptyMessage={
                Object.keys(grid.filters).length > 0 || tab !== 'ALL'
                  ? 'No orders match these filters.'
                  : 'No orders yet - they show up here once customers order through your WhatsApp ordering flow.'
              }
              renderExpanded={(o) => (expanded.has(o.id) ? <OrderDetails orderId={o.id} /> : null)}
            />
          )}
        </div>
      </Card>
    </div>
  );
}
